import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { ModelList } from '../api/types'
import { cn } from '../lib/cn'
import { controlBase } from './ui/Field'
import { Popover } from './ui/Popover'

export function ModelPicker({
  models,
  value,
  onChange,
  disabled,
  variant = 'default',
}: {
  models: ModelList | null
  value: string
  onChange(v: string): void
  disabled?: boolean
  variant?: 'default' | 'inline'
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const id = useId()
  const listId = `${id}-list`

  // Current display label
  const displayLabel = useMemo(() => {
    if (!models) return '—'
    for (const g of models.groups) {
      for (const m of g.models) {
        if (`${g.providerId}/${m}` === value) {
          return m
        }
      }
    }
    if (value) {
      return value.includes('/') ? value.split('/').slice(1).join('/') : value
    }
    return t('chat.model')
  }, [models, value, t])

  // Filtered groups according to search query
  const filteredGroups = useMemo(() => {
    if (!models) return []
    const q = query.trim().toLowerCase()
    if (!q) {
      return models.groups.filter(g => g.models.length > 0)
    }
    return models.groups
      .map(g => {
        const groupMatches = g.name.toLowerCase().includes(q)
        const matchingModels = g.models.filter(
          m => groupMatches || m.toLowerCase().includes(q)
        )
        return {
          ...g,
          models: matchingModels,
        }
      })
      .filter(g => g.models.length > 0)
  }, [models, query])

  // Flat list of selectable models for keyboard navigation
  const flatItems = useMemo(() => {
    return filteredGroups.flatMap(g =>
      g.models.map(m => ({
        value: `${g.providerId}/${m}`,
        model: m,
        providerId: g.providerId,
        providerName: g.name,
        scope: g.scope,
      }))
    )
  }, [filteredGroups])

  // Focus search input when popover opens
  useEffect(() => {
    if (open) {
      setQuery('')
      const currIdx = flatItems.findIndex(i => i.value === value)
      setActiveIndex(currIdx >= 0 ? currIdx : 0)
      requestAnimationFrame(() => {
        searchInputRef.current?.focus()
      })
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // When query changes, reset activeIndex to first match
  useEffect(() => {
    if (open) {
      setActiveIndex(0)
    }
  }, [query, open])

  // Scroll active item into view
  useEffect(() => {
    if (!open) return
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [open, activeIndex])

  const selectItem = useCallback(
    (val: string) => {
      onChange(val)
      setOpen(false)
      triggerRef.current?.focus()
    },
    [onChange]
  )

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (flatItems.length > 0) {
        setActiveIndex(idx => (idx + 1) % flatItems.length)
      }
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (flatItems.length > 0) {
        setActiveIndex(idx => (idx - 1 + flatItems.length) % flatItems.length)
      }
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (flatItems[activeIndex]) {
        selectItem(flatItems[activeIndex].value)
      }
      return
    }
    if (e.key === 'Tab') {
      setOpen(false)
      return
    }
  }

  if (!models) return null

  let globalIndexCounter = 0

  return (
    <div className={variant === 'default' ? 'w-60 max-w-full' : undefined}>
      {variant === 'default' && (
        <label htmlFor={id} className="mb-1 block text-xs font-medium text-ink-300">
          {t('chat.model')}
        </label>
      )}
      <button
        id={id}
        ref={triggerRef}
        type="button"
        role="combobox"
        // The inline variant has no visible label, so the trigger always names itself.
        aria-label={t('chat.model')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className={cn(
          'flex items-center justify-between gap-1 text-left',
          variant === 'default' && controlBase,
          variant === 'default' && 'h-8 text-xs',
          variant === 'inline' &&
            'h-7 rounded-lg px-1.5 sm:px-2 text-[12.5px] font-medium text-ink-400 hover:bg-cream-100 hover:text-ink-100 transition-colors disabled:bg-transparent disabled:opacity-50 w-auto min-w-0 max-w-full'
        )}
      >
        <span className="truncate max-w-[7.5rem] sm:max-w-[200px]">{displayLabel}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="shrink-0 opacity-60 text-ink-400"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      <Popover
        anchor={triggerRef}
        open={open}
        onClose={() => setOpen(false)}
        width={320}
        label={t('chat.model')}
        className="max-h-[75vh] sm:!max-h-[380px]"
        contentClassName="!p-0"
      >
        {/* Search header sticky at top */}
        <div className="sticky top-0 z-10 border-b border-line bg-cream-50 p-2 sm:p-2.5">
          <div className="relative flex items-center">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="pointer-events-none absolute left-3 text-ink-400"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('chat.searchModels')}
              className="w-full rounded-lg border border-cream-300 bg-cream-100/70 py-2 pl-9 pr-8 text-sm text-ink-100 placeholder:text-ink-400 hover:bg-cream-100 focus:border-ginger-500 focus:bg-cream-50 focus:outline-none"
            />
            {query && (
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setQuery('')}
                className="absolute right-2.5 rounded p-1 text-ink-400 hover:text-ink-200"
                aria-label="Clear search"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Compact group list */}
        <div
          ref={listRef}
          role="listbox"
          id={listId}
          tabIndex={-1}
          className="flex flex-col gap-1.5 p-2 focus:outline-none"
        >
          {filteredGroups.length === 0 ? (
            <div className="py-6 text-center text-sm text-ink-400">
              {t('chat.noMatchingModels')}
            </div>
          ) : (
            filteredGroups.map(g => (
              <div key={g.providerId} className="flex flex-col gap-1">
                <div className="flex select-none items-center justify-between px-2.5 pt-1.5 pb-1 text-xs font-semibold text-ink-400">
                  <span className="truncate">{g.name}</span>
                  <span className="rounded bg-cream-200/70 px-1.5 py-0.5 text-[11px] font-normal text-ink-400">
                    {g.scope === 'personal' ? t('models.personal') : t('models.shared')}
                  </span>
                </div>
                {g.models.map(m => {
                  const itemValue = `${g.providerId}/${m}`
                  const currentIndex = globalIndexCounter++
                  const isSelected = itemValue === value
                  const isActive = currentIndex === activeIndex

                  return (
                    <button
                      type="button"
                      key={itemValue}
                      role="option"
                      id={`${listId}-opt-${currentIndex}`}
                      aria-selected={isSelected}
                      data-index={currentIndex}
                      onPointerEnter={() => setActiveIndex(currentIndex)}
                      onClick={() => selectItem(itemValue)}
                      className={cn(
                        'flex min-h-9 w-full items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-left text-sm transition-colors max-sm:min-h-11 max-sm:text-[15px]',
                        isSelected
                          ? 'bg-ginger-100 font-medium text-ink-100'
                          : isActive
                          ? 'bg-cream-200 text-ink-100'
                          : 'text-ink-200 hover:bg-cream-200'
                      )}
                    >
                      <span className="truncate">{m}</span>
                      {isSelected && (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="shrink-0 text-ginger-500"
                          aria-hidden="true"
                        >
                          <path d="M5 12l5 5 9-10" />
                        </svg>
                      )}
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>
      </Popover>
    </div>
  )
}
